// Client/src/utils/stateLrs/aldot.js
// ALDOT (Alabama DOT) LRS adapter. Data source facts verified live 2026-07-16 —
// see docs/superpowers/specs/2026-07-16-lrs-milepost-route-creation-design.md
// and memory/reference_aldot_lrs_milepost_api.md.
//
// Layer 2 "Milepost"  — statewide milepost network (I-/US-/SR-), 1,610 sections.
// Layer 1 "Milepoint" — full network incl. county inventory routes, 162k sections;
//                        measures reset per county ("milepoint", not "milepost").

import {
  cardinalFromSections,
  measureRangesFromSections,
  interpolatePointAtMeasure,
  projectPointToMeasure,
} from './lrsMath';
import { AL_COUNTY_NAMES, AL_COUNTY_FIPS_BY_NAME } from './alCounties';

export const ROUTE_TYPE_LABEL = { IN: 'I', US: 'US', AL: 'SR', IV: 'IV', NC: 'NC', RP: 'Ramp' };

// ─── Normalization ────────────────────────────────────────────────────────────

/** ArcGIS query JSON → NormalizedSection[]. Handles both layer field spellings. */
export function normalizeArcgisSections(arcgisJson) {
  return (arcgisJson?.features ?? []).map((f) => {
    const a = f.attributes ?? {};
    return {
      routeName: a.RouteName ?? null,
      routeId: a.RouteId ?? a.RouteID ?? null,
      routeType: a.RouteType ?? null,
      routeNumber: Number(a.RouteNumber),
      routeDirection: Number(a.RouteDirection),
      countyCode: a.CountyCode ?? a.CountyFIPSCode ?? null,
      paths: f.geometry?.paths ?? [],
    };
  });
}

// ─── Statewide (milepost) grouping ────────────────────────────────────────────

function statewideKey(s) {
  return `milepost:${s.routeType}-${s.routeNumber}-${s.routeDirection}`;
}

export function groupStatewideSections(sections) {
  const groups = new Map();
  for (const s of sections ?? []) {
    const key = statewideKey(s);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  }
  const entries = [];
  for (const [key, group] of groups) {
    const { routeType, routeNumber, routeDirection } = group[0];
    const cardinal = cardinalFromSections(group);
    const typeLabel = ROUTE_TYPE_LABEL[routeType] ?? routeType;
    const dirLabel = cardinal ?? `Dir ${routeDirection}`;
    entries.push({
      key,
      network: 'milepost',
      label: `${typeLabel}-${routeNumber} ${dirLabel}`,
      routeType,
      routeNumber,
      routeDirection,
      countyCode: null,
      mRanges: measureRangesFromSections(group),
    });
  }
  entries.sort((a, b) =>
    a.routeType.localeCompare(b.routeType)
    || a.routeNumber - b.routeNumber
    || a.routeDirection - b.routeDirection);
  return entries;
}

// ─── Local (milepoint) entries ────────────────────────────────────────────────

export function buildLocalRouteEntries(arcgisJson) {
  return normalizeArcgisSections(arcgisJson).map((s) => {
    const typeLabel = ROUTE_TYPE_LABEL[s.routeType] ?? s.routeType;
    const county = AL_COUNTY_NAMES[s.countyCode] ?? `County ${s.countyCode}`;
    return {
      key: `milepoint:${s.routeType}-${s.routeNumber}-${s.routeDirection}-${s.countyCode}`,
      network: 'milepoint',
      label: `${typeLabel}-${s.routeNumber} (${county}) Dir ${s.routeDirection}`,
      routeType: s.routeType,
      routeNumber: s.routeNumber,
      routeDirection: s.routeDirection,
      countyCode: s.countyCode,
      mRanges: null, // unknown until geometry is fetched
    };
  });
}

// ─── Search WHERE-builder (contains semantics per spec) ───────────────────────

export function buildLocalWhere(term) {
  const cleaned = String(term ?? '').trim();
  if (!cleaned) return '1=1';

  // County names first — 'St. Clair' is multi-word, so match the whole term
  // (case/period-insensitive, word-bounded) before tokenizing the rest.
  let countyClause = null;
  let working = cleaned.toLowerCase().replace(/\./g, '');
  const countyEntries = Object.entries(AL_COUNTY_FIPS_BY_NAME)
    .map(([name, fips]) => [name.replace(/\./g, ''), fips])
    .sort((a, b) => b[0].length - a[0].length);
  for (const [normName, fips] of countyEntries) {
    const re = new RegExp(`\\b${normName}\\b`);
    if (re.test(working)) {
      countyClause = `CountyFIPSCode = '${fips}'`;
      working = working.replace(re, ' ');
      break; // one county filter per search
    }
  }

  const clauses = [];
  let consumedAll = true;
  const tokens = working.split(/[\s,-]+/).filter(Boolean);
  for (const token of tokens) {
    if (/^\d+$/.test(token)) {
      clauses.push(`RouteNumber = ${Number(token)}`);
    } else if (/^[a-z]{2}$/.test(token) && ROUTE_TYPE_LABEL[token.toUpperCase()]) {
      clauses.push(`RouteType = '${token.toUpperCase()}'`);
    } else {
      consumedAll = false;
    }
  }
  if (countyClause) clauses.push(countyClause);
  if (clauses.length > 0 && consumedAll) return clauses.join(' AND ');
  // Unrecognized content — contains match on the raw coded RouteID rather
  // than silently dropping what the user typed.
  const escaped = cleaned.toUpperCase().replace(/'/g, "''");
  return `UPPER(RouteID) LIKE '%${escaped}%'`;
}

// ─── Fetch layer / adapter ─────────────────────────────────────────────────────

const FEATURESERVER =
  'https://aldotegis.dot.state.al.us/egisrh/rest/services/Reference/EGISPro_Routes/FeatureServer';
const LAYER_MILEPOST = 2;
const LAYER_MILEPOINT = 1;
const SNAPSHOT_BASE = 'https://rsa-lrs-snapshots.s3.amazonaws.com/lrs/AL/v1';
const FETCH_TIMEOUT_MS = 8000;
const LOCAL_SEARCH_LIMIT = 50;
// Server-side generalization for the bulk statewide list (meters, native SR is
// UTM 16N). Coarse geometry is only used for labels + measure ranges; precise
// per-route geometry is fetched on selection.
const COARSE_OFFSET_METERS = 500;

export class LrsUnavailableError extends Error {
  constructor(message, detail = null) {
    super(message);
    this.name = 'LrsUnavailableError';
    this.detail = detail;
  }
}

function buildQueryUrl(layerId, params) {
  const qs = new URLSearchParams({ f: 'json', ...params });
  return `${FEATURESERVER}/${layerId}/query?${qs.toString()}`;
}

/** Parse "milepost:IN-65-1" / "milepoint:IV-308-0-073" back into query parts. */
function parseRouteKey(key) {
  const [network, rest] = String(key).split(':');
  const parts = rest.split('-');
  return {
    network,
    routeType: parts[0],
    routeNumber: Number(parts[1]),
    routeDirection: Number(parts[2]),
    countyCode: parts[3] ?? null,
  };
}

export function createAldotAdapter({ fetchImpl } = {}) {
  const doFetch = fetchImpl ?? ((...args) => fetch(...args));
  const geometryCache = new Map(); // key → { sections, mRanges }
  let statewideCache = null;       // RouteEntry[]
  let localIndexCache = null;      // RouteEntry[] — parsed local-index.json (fallback path)
  let sourceInfo = { statewideSource: null, snapshotDate: null };

  async function fetchJson(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await doFetch(url, { signal: controller.signal });
      if (!res.ok) {
        throw new LrsUnavailableError(
          'The road data service is unavailable right now.',
          `HTTP ${res.status} from ${url}`,
        );
      }
      const body = await res.json();
      // ArcGIS reports failures as HTTP 200 with an error body.
      if (body && body.error) {
        throw new LrsUnavailableError(
          'The road data service returned an error.',
          `ArcGIS error ${body.error.code}: ${body.error.message}`,
        );
      }
      return body;
    } catch (err) {
      if (err instanceof LrsUnavailableError) throw err;
      throw new LrsUnavailableError(
        'Could not reach the road data service (network problem or timeout).',
        err?.message ?? null,
      );
    } finally {
      clearTimeout(timer);
    }
  }

  /** Layer query with resultOffset pagination (ALDOT maxRecordCount is 100k,
   *  so statewide fits in one page — the loop is a safety net). */
  async function fetchAllFeatures(layerId, params) {
    const features = [];
    let offset = 0;
    for (;;) {
      const body = await fetchJson(buildQueryUrl(layerId, {
        ...params, resultOffset: String(offset),
      }));
      features.push(...(body.features ?? []));
      if (!body.exceededTransferLimit || !body.features?.length) break;
      offset += body.features.length;
    }
    return { features };
  }

  async function fetchStatewideSectionsLive() {
    const raw = await fetchAllFeatures(LAYER_MILEPOST, {
      where: '1=1',
      outFields: 'RouteId,RouteName,RouteType,RouteNumber,RouteDirection,CountyCode,RouteSection',
      returnGeometry: 'true',
      returnM: 'true',
      returnZ: 'false',
      outSR: '4326',
      maxAllowableOffset: String(COARSE_OFFSET_METERS),
    });
    return normalizeArcgisSections(raw);
  }

  async function fetchRouteSectionsLive(parsed) {
    const layerId = parsed.network === 'milepost' ? LAYER_MILEPOST : LAYER_MILEPOINT;
    const clauses = [
      `RouteType = '${parsed.routeType}'`,
      `RouteNumber = ${parsed.routeNumber}`,
      `RouteDirection = ${parsed.routeDirection}`,
    ];
    if (parsed.network === 'milepoint' && parsed.countyCode) {
      clauses.push(`CountyFIPSCode = '${parsed.countyCode}'`);
    }
    const raw = await fetchAllFeatures(layerId, {
      where: clauses.join(' AND '),
      outFields: '*',
      returnGeometry: 'true',
      returnM: 'true',
      returnZ: 'false',
      outSR: '4326',
    });
    return normalizeArcgisSections(raw);
  }

  async function getRouteGeometry(key) {
    if (geometryCache.has(key)) return geometryCache.get(key);
    const parsed = parseRouteKey(key);
    let sections = null;
    if (parsed.network === 'milepost') {
      try {
        const file = `${parsed.routeType}-${parsed.routeNumber}-${parsed.routeDirection}`;
        const snap = await fetchJson(`${SNAPSHOT_BASE}/routes/${file}.json`);
        if (Array.isArray(snap.sections) && snap.sections.length > 0) sections = snap.sections;
      } catch (_) { /* fall through to live */ }
    }
    if (!sections) {
      try {
        sections = await fetchRouteSectionsLive(parsed);
      } catch (err) {
        if (parsed.network === 'milepoint') {
          throw new LrsUnavailableError(
            'Resolving local-road milepoints requires a live ALDOT connection — currently unavailable.',
          );
        }
        throw err;
      }
    }
    const result = { sections, mRanges: measureRangesFromSections(sections) };
    geometryCache.set(key, result);
    return result;
  }

  return {
    stateCode: 'AL',
    stateName: 'Alabama (ALDOT)',
    capabilities: { speedLimits: false },

    getSourceInfo() {
      return { ...sourceInfo };
    },

    async listStatewideRoutes() {
      if (statewideCache) return statewideCache;
      let sections;
      try {
        const snap = await fetchJson(`${SNAPSHOT_BASE}/statewide-coarse.json`);
        if (!Array.isArray(snap.sections) || snap.sections.length === 0) {
          throw new LrsUnavailableError('snapshot missing sections');
        }
        sections = snap.sections;
        sourceInfo = { statewideSource: 'snapshot', snapshotDate: snap.generatedAt ?? null };
      } catch (_) {
        sections = await fetchStatewideSectionsLive(); // throws LrsUnavailableError if also down
        sourceInfo = { statewideSource: 'live', snapshotDate: null };
      }
      statewideCache = groupStatewideSections(sections);
      return statewideCache;
    },

    async searchLocalRoutes(term) {
      let raw;
      try {
        raw = await fetchJson(buildQueryUrl(LAYER_MILEPOINT, {
          where: buildLocalWhere(term),
          outFields: 'RouteID,RouteType,RouteNumber,RouteDirection,CountyFIPSCode',
          returnGeometry: 'false',
          returnDistinctValues: 'true',
          resultRecordCount: String(LOCAL_SEARCH_LIMIT),
        }));
      } catch (err) {
        // Fallback: filter the snapshot's local index client-side (contains,
        // token-AND — matches the live WHERE-builder's multi-token support).
        // The parsed index (~28 MB decompressed) is cached across calls so a
        // debounced-keystroke outage doesn't re-fetch/re-parse on every call.
        if (!localIndexCache) {
          const snap = await fetchJson(`${SNAPSHOT_BASE}/local-index.json`);
          localIndexCache = snap.roads ?? [];
        }
        const tokens = String(term ?? '').toLowerCase().split(/\s+/).filter(Boolean);
        return localIndexCache
          .filter((r) => {
            const label = r.label.toLowerCase();
            return tokens.every((t) => label.includes(t));
          })
          .slice(0, LOCAL_SEARCH_LIMIT);
      }
      return buildLocalRouteEntries(raw).slice(0, LOCAL_SEARCH_LIMIT);
    },

    getRouteGeometry,

    async measureToPoint(key, m) {
      const { sections } = await getRouteGeometry(key);
      return interpolatePointAtMeasure(sections, m);
    },

    async pointToMeasure(key, point) {
      const { sections } = await getRouteGeometry(key);
      const r = projectPointToMeasure(sections, point);
      return r ? r.m : null;
    },
  };
}

export const aldotAdapter = createAldotAdapter();
